import { AlgoliaImage } from '~/features/productsList/types';
import { ProductImageViewModel } from '~/lib/data-contract';
import styles from './product-image.module.css';
import { MediaImage, MediaImageProps } from '~/shared/components';
import { FC, memo, useMemo, useState } from 'react';
import clsx from 'clsx';

export type Variant = 'product' | 'model' | 'fallback';

type Props = Pick<MediaImageProps, 'loading' | 'priority' | 'width' | 'height' | 'fill'> & {
    image?: AlgoliaImage;
    className?: string;
    sizes?: string;
    alt?: string;
};

const getVariant = (image?: ProductImageViewModel): Variant | undefined => {
    const { label, url } = image || {};
    if (!url) {
        return 'fallback';
    }
    if (label === 'Product_detail') {
        return;
    }
    if (label?.startsWith('Product')) {
        return 'product';
    }
    if (label?.startsWith('Model')) {
        return 'model';
    }
    return 'product';
};

export const ProductImage: FC<Props> = memo(
    ({ image, className, loading, priority, sizes, alt, width, height, fill = false }) => {
        const fallbackImage = '/assets/fallback-product.svg';

        const [source, setSource] = useState(image?.url ?? fallbackImage);

        const variant = useMemo(() => getVariant(image), [image]);

        const imageStyles = clsx(styles.image, {
            [styles.productImage]: variant === 'product',
            [styles.fallBackImage]: source === fallbackImage,
        });

        return (
            <>
                <div
                    className={clsx(styles.root, className, {
                        [styles.product]: variant === 'product' || variant === undefined,
                    })}
                >
                    <MediaImage
                        className={imageStyles}
                        data-primary-image="true"
                        src={source}
                        alt={image?.label || alt || 'Product image'}
                        cW={variant === 'model' ? 2 : undefined}
                        cH={variant === 'model' ? 3 : undefined}
                        sizes={sizes}
                        loading={loading}
                        priority={priority}
                        width={width}
                        height={height}
                        fill={fill}
                        onError={() => setSource(fallbackImage)}
                    />
                </div>
            </>
        );
    }
);

ProductImage.displayName = 'ProductImage';

export default ProductImage;
